import { useState } from "react"
import { IoArrowForward, IoCheckmark } from "react-icons/io5"

export const SERVICIOS = [
    "Plan de nutrición",
    "Plan de entrenamiento",
    "Entrenamiento + nutrición para mejorar tu salud",
    "Entrenamiento + nutrición para transformar tu físico",
    "Preparación para competición",
    "Programa exclusivo para empresarios y directivos",
]

export const COMO_CONOCISTE = ["Instagram", "TikTok", "Recomendación", "Google", "Otro"]

export const WHATSAPP_PHONE = "34600803185"
const LEADS_API = `${process.env.REACT_APP_BACKEND_URL}/api/leads`

const EMPTY = {
    nombre: "",
    edad: "",
    altura: "",
    peso: "",
    servicio: "",
    telefono: "",
    email: "",
    comoConociste: "",
}

const inputCls =
    "p-3 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-sbPallet-accent"
const labelCls = "text-xs uppercase tracking-widest text-white/50"

const toPayload = (form, origen) => ({
    nombre: form.nombre.trim(),
    edad: form.edad,
    altura: form.altura,
    peso: form.peso,
    servicio: form.servicio,
    telefono: form.telefono.trim(),
    email: form.email.trim(),
    como_conociste: form.comoConociste || null,
    origen,
})

export const LeadSuccess = ({ nombre }) => (
    <div
        className="w-full max-w-lg mt-10 bg-sbPallet-cardBg border border-white/10 rounded-2xl p-8 lg:p-10 flex flex-col items-center text-center animate-in fade-in slide-in-from-bottom-2 duration-500"
        data-testid="lead-form-success"
        role="status"
    >
        <img src="/assets/RaulPerezLogoFinal.png" alt="Raúl Pérez Coach" className="h-12 lg:h-14 w-auto" />
        <div className="mt-8 h-14 w-14 rounded-full bg-sbPallet-accent/15 flex items-center justify-center">
            <IoCheckmark className="text-sbPallet-accent" size={28} />
        </div>
        <h3 className="mt-6 text-xl lg:text-2xl font-black uppercase leading-tight" data-testid="lead-form-success-title">
            Solicitud enviada correctamente{nombre ? `, ${nombre}` : ""}.
        </h3>
        <p className="mt-3 text-white/70">En breve me pondré en contacto contigo.</p>
        <p className="mt-8 text-[11px] uppercase tracking-widest text-white/40 font-bold">Raúl Pérez · Health &amp; Performance Coach</p>
    </div>
)

/**
 * Formulario de captación compartido (página principal y /solicitud).
 * - origen: "landing" | "intake"
 * Guarda el lead en el panel y muestra la pantalla de confirmación (no abre WhatsApp).
 * - onSuccess: se llama tras guardar correctamente
 */
const LeadForm = ({ origen = "landing", onSuccess, submitLabel = "Enviar solicitud" }) => {
    const [form, setForm] = useState(EMPTY)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState("")
    const [doneName, setDoneName] = useState(null)

    const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))
    const handleServicio = (servicio) => () => {
        setForm((prev) => ({ ...prev, servicio }))
        setError("")
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!form.servicio) {
            setError("Selecciona el servicio que te interesa.")
            return
        }
        setError("")
        const body = JSON.stringify(toPayload(form, origen))

        setSending(true)
        try {
            const res = await fetch(LEADS_API, { method: "POST", headers: { "Content-Type": "application/json" }, body })
            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                const badEmail = Array.isArray(data?.detail) && data.detail.some((d) => (d.loc || []).includes("email"))
                throw new Error(badEmail ? "Revisa el correo electrónico." : "Revisa los datos e inténtalo de nuevo.")
            }
            setDoneName(form.nombre.trim())
            onSuccess && onSuccess(form)
        } catch (err) {
            setError(err.message || "No se pudo enviar. Inténtalo de nuevo.")
        } finally {
            setSending(false)
        }
    }

    if (doneName !== null) return <LeadSuccess nombre={doneName} />

    return (
        <form
            onSubmit={handleSubmit}
            data-testid={`lead-form-${origen}`}
            className="w-full max-w-lg mt-10 bg-sbPallet-cardBg border border-white/10 rounded-2xl p-6 lg:p-8 flex flex-col gap-6"
        >
            <div className="flex flex-col gap-2">
                <label htmlFor={`${origen}-nombre`} className={labelCls}>Nombre</label>
                <input id={`${origen}-nombre`} className={inputCls} type="text" placeholder="Tu nombre" autoComplete="name"
                       value={form.nombre} onChange={handleChange("nombre")} maxLength={120} required data-testid="lead-form-nombre" />
            </div>

            <div className="grid grid-cols-3 gap-3">
                <div className="flex flex-col gap-2">
                    <label htmlFor={`${origen}-edad`} className={labelCls}>Edad</label>
                    <input id={`${origen}-edad`} className={inputCls} type="number" min="0" max="120" placeholder="28" inputMode="numeric"
                           value={form.edad} onChange={handleChange("edad")} required data-testid="lead-form-edad" />
                </div>
                <div className="flex flex-col gap-2">
                    <label htmlFor={`${origen}-altura`} className={labelCls}>Altura (cm)</label>
                    <input id={`${origen}-altura`} className={inputCls} type="number" min="0" max="260" placeholder="175" inputMode="numeric"
                           value={form.altura} onChange={handleChange("altura")} required data-testid="lead-form-altura" />
                </div>
                <div className="flex flex-col gap-2">
                    <label htmlFor={`${origen}-peso`} className={labelCls}>Peso (kg)</label>
                    <input id={`${origen}-peso`} className={inputCls} type="number" min="0" max="400" step="0.1" placeholder="78" inputMode="decimal"
                           value={form.peso} onChange={handleChange("peso")} required data-testid="lead-form-peso" />
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <span className={labelCls}>¿Qué servicio te interesa?</span>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Servicio">
                    {SERVICIOS.map((servicio, i) => (
                        <button
                            key={servicio}
                            type="button"
                            role="radio"
                            aria-checked={form.servicio === servicio}
                            onClick={handleServicio(servicio)}
                            data-testid={`lead-form-servicio-${i}`}
                            className={`py-2 px-4 rounded-full text-sm border ease-in duration-100 ${
                                form.servicio === servicio
                                    ? "bg-sbPallet-accent border-sbPallet-accent text-white"
                                    : "bg-transparent border-white/15 text-white/70 hover:border-sbPallet-accent hover:text-white"
                            }`}
                        >
                            {servicio}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <label htmlFor={`${origen}-telefono`} className={labelCls}>Teléfono (WhatsApp)</label>
                <input id={`${origen}-telefono`} className={inputCls} type="tel" placeholder="600 000 000" autoComplete="tel" inputMode="tel"
                       value={form.telefono} onChange={handleChange("telefono")} minLength={6} maxLength={40} required data-testid="lead-form-telefono" />
            </div>

            <div className="flex flex-col gap-2">
                <label htmlFor={`${origen}-email`} className={labelCls}>Correo electrónico</label>
                <input id={`${origen}-email`} className={inputCls} type="email" placeholder="tu@email.com" autoComplete="email"
                       value={form.email} onChange={handleChange("email")} required data-testid="lead-form-email" />
            </div>

            <div className="flex flex-col gap-2">
                <label htmlFor={`${origen}-como`} className={labelCls}>¿Cómo me has conocido? (opcional)</label>
                <select id={`${origen}-como`}
                        className="p-3 rounded-lg bg-black/30 border border-white/10 text-sm text-white/80 focus:outline-none focus:border-sbPallet-accent"
                        value={form.comoConociste} onChange={handleChange("comoConociste")} data-testid="lead-form-como">
                    <option value="" className="bg-sbPallet-cardBg">Selecciona una opción</option>
                    {COMO_CONOCISTE.map((o) => (
                        <option key={o} value={o} className="bg-sbPallet-cardBg">{o}</option>
                    ))}
                </select>
            </div>

            {error && (
                <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="lead-form-error">
                    {error}
                </p>
            )}

            <button
                type="submit"
                disabled={sending}
                data-testid="lead-form-submit"
                className="w-full py-3 px-4 rounded-lg bg-sbPallet-accent hover:bg-white hover:text-sbPallet-accent disabled:opacity-70 shadow-[0_0_25px_rgba(250,58,16,0.45)] font-bold uppercase flex items-center justify-center gap-2 ease-in duration-100"
            >
                {sending ? "Enviando…" : submitLabel} {!sending && <IoArrowForward size={20} />}
            </button>

            <div className="flex flex-col items-center gap-1 text-xs text-white/60 text-center">
                <p className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
                    <span>✓ Plan personalizado</span>
                    <span>✓ Seguimiento por WhatsApp</span>
                </p>
                <p className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
                    <span>✓ Adaptado a tu horario</span>
                    <span>✓ Sin dietas extremas</span>
                </p>
            </div>

            <p className="text-center text-[11px] text-white/40">
                Tus datos solo se usan para contactarte. Sin spam, sin compromiso.
            </p>
        </form>
    )
}

export default LeadForm
